/**
 * Evaluation Metrics API
 * Metric definition management and prompt viewing
 */

import apiClient from './client';
import type {
  MetricDefinition,
  MetricDefinitionListResponse,
  CreateMetricDefinitionRequest,
  UpdateMetricDefinitionRequest,
  MetricPromptResponse,
  MetricGenerateRequest,
  MetricGenerateResponse,
} from '@/types/evalScenarios';

export const evalMetricsApi = {
  /**
   * List all metric definitions with optional filters
   */
  list: async (params?: {
    metric_type?: string;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<MetricDefinitionListResponse> => {
    const response = await apiClient.get<MetricDefinitionListResponse>('/eval-metrics', { params });
    return response.data;
  },

  /**
   * Get a single metric definition by name
   */
  get: async (name: string): Promise<MetricDefinition> => {
    const response = await apiClient.get<MetricDefinition>(`/eval-metrics/${name}`);
    return response.data;
  },

  /**
   * Get the full prompt for a metric (including builtin prompts)
   */
  getPrompt: async (name: string): Promise<MetricPromptResponse> => {
    const response = await apiClient.get<MetricPromptResponse>(`/eval-metrics/${name}/prompt`);
    return response.data;
  },

  /**
   * Create a new custom metric definition
   */
  create: async (data: CreateMetricDefinitionRequest): Promise<MetricDefinition> => {
    const response = await apiClient.post<MetricDefinition>('/eval-metrics', data);
    return response.data;
  },

  /**
   * Update an existing metric definition
   */
  update: async (name: string, data: UpdateMetricDefinitionRequest): Promise<MetricDefinition> => {
    const response = await apiClient.put<MetricDefinition>(`/eval-metrics/${name}`, data);
    return response.data;
  },

  /**
   * Delete a custom metric definition
   */
  delete: async (name: string): Promise<void> => {
    await apiClient.delete(`/eval-metrics/${name}`);
  },

  /**
   * Get metrics applicable to a specific agent type
   */
  getForAgent: async (agentType: string): Promise<{ agent_type: string; metrics: MetricDefinition[] }> => {
    const response = await apiClient.get<{ agent_type: string; metrics: MetricDefinition[] }>(
      `/eval-metrics/for-agent/${agentType}`
    );
    return response.data;
  },

  /**
   * Generate metric criteria and steps using AI
   */
  generate: async (data: MetricGenerateRequest): Promise<MetricGenerateResponse> => {
    const response = await apiClient.post<MetricGenerateResponse>('/eval-metrics/generate', data);
    return response.data;
  },
};
